<script setup lang="ts">
import FormError from '@/components/formError/FormError.vue';
import Label from '@/components/label/Label.vue';
import { uniqueId } from 'lodash';

interface RadioInput {
  error?: string;
  helperText?: string;
  label: string;
  value: string | boolean | null;
}

export type RadioInputProps = {
  ariaLabel: string;
  hasError?: boolean;
  isDisabled?: boolean;
  name: string;
  radioInputs: RadioInput[];
  value?: string | boolean | null;
  direction?: 'horizontal' | 'vertical';
  variant?: 'outlined' | 'invisible';
};

const props = withDefaults(defineProps<RadioInputProps>(), {
  direction: 'vertical',
  variant: 'invisible'
});

const uniqueName = computed(() => uniqueId(props.name));

const emit = defineEmits<{
  'update:modelValue': [value: string | boolean | null];
}>();

function onInput(event: string | boolean | null) {
  emit('update:modelValue', event);
}
</script>

<template>
  <div
    class="flex"
    :class="{
      'flex-col  gap-1': direction === 'vertical',
      'flex-row  gap-2.5': direction === 'horizontal'
    }"
  >
    <label
      v-for="radioInput in radioInputs"
      :key="String(radioInput.value)"
      :disabled="isDisabled"
      :class="[
        'group relative flex cursor-pointer items-start gap-2 transition-input duration-150 ease-in-out',
        'disabled:cursor-not-allowed',
        {
          'py-1': direction === 'vertical',
          'rounded-lg border border-slate-200 bg-white px-3 py-2 hover:border-slate-700 has-[[checked]]:border-secondary-400  has-[[data-disabled][checked]]:border-secondary-400/50 has-[[data-disabled]]:border-slate-200/50  has-[[data-disabled][checked]]:hover:border-secondary-400/50':
            variant === 'outlined'
        }
      ]"
    >
      <div
        class="flex flex-row items-start gap-2"
        :class="{
          'py-1': variant === 'outlined',
          'py-0.5 md:py-0': variant === 'invisible'
        }"
      >
        <input
          type="radio"
          :aria-label="ariaLabel"
          :name="uniqueName"
          :disabled="isDisabled"
          :class="[
            'h-5.5 w-5.5 cursor-pointer rounded-full border-slate-300 text-white md:h-4.5 md:w-4.5',
            'transition-all duration-150 ease-in-out',
            'checked:border-secondary-800 checked:text-white',
            'focus:ring-3 focus:ring-secondary-200 focus:ring-offset-0 checked:focus:border-secondary-800',
            'group-hover:border-slate-300 group-hover:bg-secondary-50 group-hover:ring-3 group-hover:ring-secondary-50',
            'group-hover:checked:border-secondary-800',
            'group-hover:disabled:border-slate-300 group-hover:disabled:bg-transparent group-hover:disabled:ring-0 group-hover:disabled:checked:border-secondary-800',
            hasError && 'errored !border-red-50 focus:ring-red-200 group-hover:ring-red-200'
          ]"
          :value="radioInput.value"
          :checked="radioInput.value === value"
          @input="() => onInput(radioInput.value)"
        />

        <div
          class="flex flex-col gap-px pt-0.5 md:gap-0 md:py-0"
          :class="{
            'opacity-50 group-hover:cursor-not-allowed': isDisabled
          }"
        >
          <Label as="div" class="leading-5">
            {{ radioInput.label }}
          </Label>
          <p
            v-if="radioInput.helperText"
            class="select-none text-md leading-5 text-slate-500 md:text-sm md:leading-4"
          >
            {{ radioInput.helperText }}
          </p>
          <FormError v-if="radioInput.error" class="leading-5" hasIcon>
            {{ radioInput.error }}
          </FormError>
        </div>
      </div>
    </label>
  </div>
</template>

<style scoped>
[type='radio']:checked {
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='%230f4499' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='8' cy='8' r='4'/%3e%3c/svg%3e");
}
[type='radio']:checked.errored {
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='%23b91c1c' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='8' cy='8' r='4'/%3e%3c/svg%3e");
}
</style>
